import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  FillTheBlanksQuestionCreateRequestDto,
  MatchQuestionCreateRequestDto,
  OpenEndedQuestionCreateRequestDto,
  TestQuestionCreateRequestDto,
  TrueFalseQuestionCreateRequestDto,
  QuestionCreateRequestDtoDifficulty,
  BaseAssignmentResponseDtoQuestionsItem,
  BaseQuestionDtoType,
  BaseFillTheBlankQuestionDto,
  BaseTestQuestionDto,
  BaseMatchQuestionDto,
  BaseOpenEndedQuestionDto,
  BaseTrueFalseQuestionDto,
} from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/button";
import Modal from "@core/modal";

import matchIcon from "@packages/assets/match.png";
import multipleIcon from "@packages/assets/multiple.png";
import optionIcon from "@packages/assets/option.png";
import voteIcon from "@packages/assets/vote.png";
import openEndedIcon from "@packages/assets/open_ended_icon.png";
import { QuestionEditor } from "../SingleAssignment/componnets/questions/QuestionEditor";

interface QuestionAddModalProps {
  onAddQuestion: (
    newQuestion:
      | FillTheBlanksQuestionCreateRequestDto
      | MatchQuestionCreateRequestDto
      | OpenEndedQuestionCreateRequestDto
      | TestQuestionCreateRequestDto
      | TrueFalseQuestionCreateRequestDto,
  ) => void;
  disabled?: boolean;
  gradeLevel?: number;
}

interface QuestionCardConfig {
  apiType: keyof typeof BaseQuestionDtoType;
  icon: string;
  translationKey: string;
}

export default function QuestionAddModal({
  onAddQuestion,
  disabled = false,
  gradeLevel,
}: QuestionAddModalProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState<number>(1);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] =
    useState<BaseAssignmentResponseDtoQuestionsItem | null>(null);

  const questionCards = useMemo<Record<string, QuestionCardConfig>>(() => {
    return {
      [BaseQuestionDtoType.TEST]: {
        apiType: "TEST",
        icon: multipleIcon,
        translationKey: "multipleChoice",
      },
      [BaseQuestionDtoType.TF]: {
        apiType: "TF",
        icon: voteIcon,
        translationKey: "trueFalse",
      },
      [BaseQuestionDtoType.OE]: {
        apiType: "OE",
        icon: openEndedIcon,
        translationKey: "openEnded",
      },
      // [BaseQuestionDtoType.MATCH]: {
      //   apiType: "MATCH",
      //   icon: matchIcon,
      //   translationKey: "matching",
      // },
      [BaseQuestionDtoType.FB]: {
        apiType: "FB",
        icon: optionIcon,
        translationKey: "fillInBlank",
      },
    };
  }, []);

  const CustomCard = ({
    type,
    config,
  }: {
    type: string;
    config: QuestionCardConfig;
  }) => {
    const isHovered = hoveredCard === type;
    const title = t(`assignments.questionTypes.${config.translationKey}.title`);
    const description = t(
      `assignments.questionTypes.${config.translationKey}.description`,
    );

    return (
      <div
        onClick={() =>
          selectQuestionType(type as keyof typeof BaseQuestionDtoType)
        }
        onMouseEnter={() => setHoveredCard(type)}
        onMouseLeave={() => setHoveredCard(null)}
        className="mx-auto w-full transition-all duration-300 ease-in-out cursor-pointer"
      >
        <div
          className={`flex flex-col items-center p-6 rounded-xl border ${
            isHovered
              ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md transform -translate-y-1"
              : "border-gray-200 dark:border-gray-700 hover:border-primary-300"
          } transition-all duration-300 h-full`}
        >
          <div className="flex justify-center items-center w-16 h-16 mb-4 rounded-full bg-primary-100 dark:bg-primary-900/30 transition-all duration-300 ease-in-out group-hover:scale-105">
            <img
              src={config.icon}
              alt={title}
              className="w-8 h-8 transition-transform duration-300 ease-in-out group-hover:scale-110"
            />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            {description}
          </p>
        </div>
      </div>
    );
  };

  const createEmptyTestQuestion = (): TestQuestionCreateRequestDto => ({
    body: "",
    point: 10,
    explanation: "",
    type: BaseQuestionDtoType.TEST,
    difficulty: QuestionCreateRequestDtoDifficulty.EASY,
    answer: "A",
    options: {
      A: "",
      B: "",
      C: "",
      D: "",
      ...(gradeLevel && gradeLevel >= 9 && gradeLevel <= 12 ? { E: "" } : {}),
    },
  });

  const createEmptyTrueFalseQuestion =
    (): TrueFalseQuestionCreateRequestDto => ({
      body: "",
      point: 5,
      explanation: "",
      type: BaseQuestionDtoType.TF,
      difficulty: QuestionCreateRequestDtoDifficulty.EASY,
      answer: true,
    });

  const createEmptyOpenEndedQuestion =
    (): OpenEndedQuestionCreateRequestDto => ({
      body: "",
      point: 15,
      explanation: "",
      type: BaseQuestionDtoType.OE,
      difficulty: QuestionCreateRequestDtoDifficulty.EASY,
      answer: "",
    });

  const createEmptyFillInBlanksQuestion =
    (): FillTheBlanksQuestionCreateRequestDto => ({
      body: "",
      point: 10,
      explanation: "",
      type: BaseQuestionDtoType.FB,
      difficulty: QuestionCreateRequestDtoDifficulty.EASY,
      answer: [""],
      possibleAnswers: [""],
    });

  const createEmptyMatchingQuestion = (): MatchQuestionCreateRequestDto => ({
    body: "",
    point: 10,
    explanation: "",
    type: BaseQuestionDtoType.MATCH,
    difficulty: QuestionCreateRequestDtoDifficulty.EASY,
    leftPart: {
      "1": "",
      "2": "",
      "3": "",
      "4": "",
    },
    rightPart: {
      A: "",
      B: "",
      C: "",
      D: "",
    },
    answer: {
      "1": "A",
      "2": "B",
      "3": "C",
      "4": "D",
    },
  });

  const selectQuestionType = (type: keyof typeof BaseQuestionDtoType) => {
    let newQuestion;

    switch (type) {
      case BaseQuestionDtoType.TEST:
        newQuestion = createEmptyTestQuestion();
        break;
      case BaseQuestionDtoType.TF:
        newQuestion = createEmptyTrueFalseQuestion();
        break;
      case BaseQuestionDtoType.OE:
        newQuestion = createEmptyOpenEndedQuestion();
        break;
      case BaseQuestionDtoType.FB:
        newQuestion = createEmptyFillInBlanksQuestion();
        break;
      case BaseQuestionDtoType.MATCH:
        newQuestion = createEmptyMatchingQuestion();
        break;
      default:
        return;
    }

    const questionWithId = {
      ...newQuestion,
      id: -1,
      dislikes: 0,
      likes: 0,
    } as unknown as BaseAssignmentResponseDtoQuestionsItem;

    setSelectedQuestion(questionWithId);
    setStep(2);
  };

  const handleSubmitQuestion = () => {
    if (selectedQuestion) {
      const extractPropertiesForDTO = () => {
        const {
          id,
          likes,
          dislikes,
          learningOutcome,
          assignmentId,
          ...baseProps
        } = selectedQuestion;

        switch (selectedQuestion.type) {
          case BaseQuestionDtoType.TEST: {
            const testQuestion =
              selectedQuestion as unknown as BaseTestQuestionDto;
            return {
              ...baseProps,
              answer: testQuestion.answer,
              options: testQuestion.options,
            } as TestQuestionCreateRequestDto;
          }
          case BaseQuestionDtoType.TF: {
            const tfQuestion =
              selectedQuestion as unknown as BaseTrueFalseQuestionDto;
            return {
              ...baseProps,
              answer: tfQuestion.answer,
            } as TrueFalseQuestionCreateRequestDto;
          }
          case BaseQuestionDtoType.OE: {
            const oeQuestion =
              selectedQuestion as unknown as BaseOpenEndedQuestionDto;
            return {
              ...baseProps,
              answer: oeQuestion.answer,
            } as OpenEndedQuestionCreateRequestDto;
          }
          case BaseQuestionDtoType.FB: {
            const fbQuestion =
              selectedQuestion as unknown as BaseFillTheBlankQuestionDto;
            return {
              ...baseProps,
              answer: fbQuestion.answer,
              possibleAnswers: fbQuestion.possibleAnswers || fbQuestion.answer,
            } as FillTheBlanksQuestionCreateRequestDto;
          }
          case BaseQuestionDtoType.MATCH: {
            const matchQuestion =
              selectedQuestion as unknown as BaseMatchQuestionDto;
            return {
              ...baseProps,
              answer: matchQuestion.answer,
              leftPart: matchQuestion.leftPart,
              rightPart: matchQuestion.rightPart,
            } as MatchQuestionCreateRequestDto;
          }
          default:
            return baseProps;
        }
      };

      const questionDTO = extractPropertiesForDTO();
      onAddQuestion(questionDTO as any);
      resetModal();
      setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 100);
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedQuestion(null);
    setShowModal(false);
    setHoveredCard(null);
  };

  const handleQuestionChange = (
    updatedQuestion: BaseAssignmentResponseDtoQuestionsItem,
  ) => {
    setSelectedQuestion(updatedQuestion);
  };

  const renderQuestionTypeSelection = () => (
    <div className="p-6 animate-fadeIn">
      <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4 animate-fadeIn">
        {t("assignments.questionSelectionPrompt")}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 animate-fadeIn">
        {Object.entries(questionCards).map(([type, config]) => (
          <CustomCard key={type} type={type} config={config} />
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={resetModal}
          size="md"
          rounded="lg"
          variant="outline"
          className="mr-2"
        >
          {t("assignments.actions.cancel")}
        </Button>
      </div>
    </div>
  );

  const renderQuestionEditor = () => (
    <div className="p-6 animate-fadeIn">
      {selectedQuestion && (
        <>
          <QuestionEditor
            question={selectedQuestion}
            onQuestionChange={handleQuestionChange}
            gradeLevel={gradeLevel}
          />

          <div className="mt-8 flex justify-between">
            <Button
              onClick={() => setStep(1)}
              size="md"
              rounded="lg"
              variant="outline"
              leftIcon={<ArrowLeftIcon className="h-4 w-4 mr-2" />}
            >
              {t("assignments.actions.back")}
            </Button>

            <Button
              onClick={handleSubmitQuestion}
              size="md"
              rounded="lg"
              textColor="text-white"
              bgColor="bg-primary-500 hover:bg-primary-600"
            >
              {t("assignments.actions.addQuestion")}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const getModalTitle = () => {
    if (step === 1) {
      return t("assignments.selectQuestionType");
    } else if (selectedQuestion) {
      const typeConfig =
        questionCards[
          selectedQuestion.type as keyof typeof BaseQuestionDtoType
        ];

      if (typeConfig) {
        return `${t("assignments.addQuestion")}: ${t(`assignments.questionTypes.${typeConfig.translationKey}.title`)}`;
      }

      return t("assignments.addQuestion");
    }

    return t("assignments.addQuestion");
  };

  return (
    <>
      <Button
        onClick={() => !disabled && setShowModal(true)}
        disabled={disabled}
        size="md"
        rounded="lg"
        leftIcon={<PlusIcon className="h-5 w-5 ml-2" />}
        textColor="text-white"
        bgColor="bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 disabled:text-gray-500"
      >
        {t("assignments.addQuestion")}
      </Button>

      <Modal
        show={showModal}
        onClose={resetModal}
        title={getModalTitle()}
        outerClassName="max-w-4xl max-h-[700px] overflow-y-auto"
      >
        {step === 1 ? renderQuestionTypeSelection() : renderQuestionEditor()}
      </Modal>
    </>
  );
}
